import type {
  CreateIntentDto,
  CreateProposalDto,
  SegmentProposal,
  SegmentResult,
  SparkIntent,
  SparkIntentListItem,
  SparkPrioritizedIntentResults,
  SparkSegment,
  SparkWeekPreview,
  UpdateIntentAvailabilityByDateDto,
  UpdateIntentAvailabilityHorizonDto,
} from '@/core/data/spark/spark.interface';
import { SparksEndpoint } from '@/core/network/api/constants/api.url.constant';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export interface RespondSparkInvitationDto {
  proposalId: string;
  date: string;
}

export interface IntentResultOptions {
  afterCursor?: string | null;
}

export class SparkApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async createIntent(payload: CreateIntentDto): Promise<AxiosResponse<GamResponse<SparkIntent>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_INTENTS, {
      data: { ...payload, radiusMeters: Math.floor(payload.radiusMeters) },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async updateIntent(intentId: string, payload: CreateIntentDto): Promise<AxiosResponse<GamResponse<SparkIntent>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_INTENT, {
      urlParams: {
        id: intentId,
      },
      data: { ...payload, radiusMeters: Math.floor(payload.radiusMeters) },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async markProposalSuccessAsRead(proposalId: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_MARK_SUCCESS_AS_READ, {
      urlParams: {
        id: proposalId,
      },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async updateIntentAvailabilityByDate(
    dateString: string,
    payload: UpdateIntentAvailabilityByDateDto,
  ): Promise<AxiosResponse<GamResponse<SparkIntent>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_INTENT_DATE_AVAILABILITY, {
      urlParams: {
        date: dateString,
      },
      data: payload,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async updateIntentAvailabilityHorizon(
    payload: UpdateIntentAvailabilityHorizonDto,
  ): Promise<AxiosResponse<GamResponse<SparkIntent[]>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_INTENT_AVAILABILITY_HORIZON, {
      data: payload,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async initializeAvailabilityHorizon(cityId: string): Promise<AxiosResponse<GamResponse<SparkIntent[]>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_INTENT_AVAILABILITY_HORIZON_INIT, {
      data: { cityId },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async deleteIntent(intentId: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.delete(SparksEndpoint.SPARK_INTENT, {
      urlParams: {
        id: intentId,
      },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async createProposal(payload: CreateProposalDto): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSALS, {
      data: payload,
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async createProposalInvitations(proposalId: string, userIds: string[]): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_INVITATIONS, {
      urlParams: { id: proposalId },
      data: { userIds },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async acceptProposalInvitation(
    proposalId: string,
    chessSetCount?: number,
  ): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_ACCEPT_INVITATION, {
      urlParams: { id: proposalId },
      data: { chessSetCount: chessSetCount ?? 0 },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async declineProposalInvitation(proposalId: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_DECLINE_INVITATION, {
      urlParams: { id: proposalId },
    });
  }

  async withdrawProposalInvitation(proposalId: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_WITHDRAW_INVITATION, {
      urlParams: { id: proposalId },
    });
  }

  async getProposal(id: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return this.httpClient.get<SegmentProposal>(SparksEndpoint.SPARK_PROPOSAL, {
      urlParams: { id },
      config: {
        // params,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async createConfirmation(proposalId: string, intentId: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_CONFIRMATIONS, {
      data: {
        proposalId,
        intentId,
      },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async deleteConfirmation(proposalId: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.delete(SparksEndpoint.SPARK_PROPOSAL_CONFIRMATION, {
      urlParams: {
        id: proposalId,
      },
    });
  }

  async updateConfirmationChessSetCount(
    proposalId: string,
    chessSetCount: number,
  ): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_PROPOSAL_CONFIRMATION, {
      urlParams: { id: proposalId },
      data: { chessSetCount },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async meetAnyways(proposalId: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_PROPOSAL_MEET_ANYWAYS, {
      urlParams: { id: proposalId },
    });
  }

  async undoMeetAnyways(proposalId: string): Promise<AxiosResponse<GamResponse<SegmentProposal>>> {
    return await this.httpClient.delete(SparksEndpoint.SPARK_PROPOSAL_MEET_ANYWAYS, {
      urlParams: { id: proposalId },
    });
  }

  async getIntent(intentId: string): Promise<AxiosResponse<GamResponse<SparkIntent>>> {
    return await this.httpClient.get(SparksEndpoint.SPARK_INTENT, {
      urlParams: { id: intentId },
    });
  }

  async getIntentByDate(dateString: string): Promise<AxiosResponse<GamResponse<SparkIntent>>> {
    return await this.httpClient.get(SparksEndpoint.SPARK_INTENT_DATE, {
      urlParams: { date: dateString },
    });
  }

  async getWeekPreview(from: string): Promise<AxiosResponse<GamResponse<SparkWeekPreview>>> {
    return this.httpClient.get<SparkWeekPreview>(SparksEndpoint.SPARK_WEEK_PREVIEW, {
      config: { params: { from } },
    });
  }

  async getIntents(): Promise<AxiosResponse<GamResponse<SparkIntentListItem[]>>> {
    return await this.httpClient.get(SparksEndpoint.SPARK_INTENTS, {
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async extendAvailabilities(cityId: string): Promise<AxiosResponse<GamResponse<SparkIntent[]>>> {
    return await this.httpClient.post(SparksEndpoint.SPARK_EXTEND_AVAILABILITIES, {
      data: { city_id: cityId },
      config: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
  }

  async findMatches(
    locationIds: string[],
    start: Date,
    end: Date,
  ): Promise<AxiosResponse<GamResponse<SparkSegment[]>>> {
    return this.httpClient.get<SparkSegment[]>(SparksEndpoint.SPARK_MATCHES, {
      config: {
        params: {
          locationIds: locationIds.join(','),
          start: start.toISOString(),
          end: end.toISOString(),
        },
      },
    });
  }

  async findIntentResults(
    intentId: string,
    options?: IntentResultOptions,
  ): Promise<AxiosResponse<GamResponse<SegmentResult[]>>> {
    return this.httpClient.get<SegmentResult[]>(SparksEndpoint.SPARK_INTENT_RESULTS, {
      urlParams: {
        id: intentId,
      },
      queryParams: options?.afterCursor ? { afterCursor: options.afterCursor } : {},
    });
  }

  async findPrioritizedIntentResults(
    intentId: string,
    gpsCoords?: { lat: number; lng: number },
  ): Promise<AxiosResponse<GamResponse<SparkPrioritizedIntentResults>>> {
    return this.httpClient.get<SparkPrioritizedIntentResults>(SparksEndpoint.SPARK_INTENT_RESULTS_PRIORITIZED, {
      urlParams: {
        id: intentId,
      },
      queryParams: gpsCoords ? { lat: String(gpsCoords.lat), lng: String(gpsCoords.lng) } : {},
    });
  }

  async getRespondSparkInvitations(): Promise<AxiosResponse<GamResponse<RespondSparkInvitationDto[]>>> {
    return this.httpClient.get<RespondSparkInvitationDto[]>(SparksEndpoint.SPARK_INVITATIONS_RESPOND);
  }

  async createFirestoreProposalDoc(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_PROPOSAL_FIRESTORE, {
      urlParams: { id },
    });
  }

  async createFirestoreIntentDoc(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(SparksEndpoint.SPARK_INTENT_FIRESTORE, {
      urlParams: { id },
    });
  }
}
